/**
 * Agent Proposal Flow Component
 *
 * Implements the propose → simulate → gated outcome flow
 * using the agent gateway module.
 *
 * Flow:
 * 1. User creates proposal
 * 2. System simulates outcome
 * 3. User reviews simulation
 * 4. If approved, create execution request
 * 5. Evaluate through gateways
 * 6. Show final outcome (EXECUTED or BLOCKED)
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Loader2,
  X,
  Brain,
  Github,
  ExternalLink
} from 'lucide-react';
import { DecisionPackageV1 } from '../viewer/decisionPackageTypes';

type ProposalStep = 'create' | 'simulate' | 'review' | 'validate' | 'result';

type SimulationOutcome = 'ALLOW' | 'BLOCK';
type ProposalStatus = 'VALID' | 'BLOCKED';

import { ScenarioData } from '../utils/validatePlaygroundFork';
import { ForkSuggestion } from '../utils/deterministicSuggestions';

interface AgentProposalFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onExecutionComplete?: (proposal: any, result: any) => void;
  preselectedChange?: DemoChange | null;
  scenarioData?: ScenarioData | null;
  forkSuggestion?: ForkSuggestion | null;
  isForkConnected?: boolean;
}

// Concrete demo change templates
interface DemoChange {
  title: string;
  file: string;
  diff: string;
  outcome: SimulationOutcome;
  summary: string;
  newState: string[];
  nextStep: string;
  blockReasons?: string[];
}

const DEMO_CHANGES: DemoChange[] = [
  {
    title: 'Review patch for dashboard crash on null GitHub data',
    file: 'src/components/Dashboard.tsx',
    diff: `@@ -120,7 +120,15 @@ function Dashboard({ data }) {
-  const repos = data.github.repositories;
+  const repos = data?.github?.repositories || [];
+
+  if (!data?.github) {
+    console.warn("GitHub data not available — using fallback state");
+  }

   return (
     <div>
-      {repos.map(repo => (
+      {repos.length === 0 ? (
+        <div>No repositories connected yet</div>
+      ) : (
+        repos.map(repo => (
           <RepoCard key={repo.id} repo={repo} />
-      ))}
+        ))
+      )}
     </div>
   );
 }`,
    outcome: 'ALLOW',
    summary: 'This change prevents a potential crash when GitHub data is missing or undefined. Your dashboard will continue to work even if no repositories are connected.',
    newState: [
      'Dashboard no longer crashes on null GitHub data',
      'Empty state is shown instead of breaking',
      'Improved stability for first-time users'
    ],
    nextStep: 'Review the generated Dashboard null-safety patch, or copy it locally to inspect first.'
  },
  {
    title: 'Review patch for API validation to prevent invalid requests',
    file: 'src/api/routes/index.ts',
    diff: `@@ -42,6 +42,12 @@ router.post("/api/proposal", async (req, res) => {
-  const { intent, action } = req.body;
+  const { intent, action } = req.body;
+
+  if (!intent || !action) {
+    return res.status(400).json({
+      error: "Missing required fields: intent and action"
+    });
+  }

   const proposal = await createProposal(intent, action);
   res.json(proposal);
 });`,
    outcome: 'ALLOW',
    summary: 'This change prevents invalid requests from reaching your agent system. Missing inputs are now rejected early with clear errors.',
    newState: [
      'Invalid API requests are blocked',
      'Clear error responses returned to clients',
      'Reduced risk of runtime failures'
    ],
    nextStep: 'Review the generated API validation patch, or copy it locally to inspect first.'
  },
  {
    title: 'Bypass approval to deploy changes (unsafe)',
    file: 'src/deploy/autoDeploy.ts',
    diff: `@@ -15,6 +15,10 @@ export async function deploy(change) {
-  const approved = await requestApproval(change);
-  if (!approved) return;
+  // Skip approval check
+  console.log("Auto-deploying without approval");

   await applyChange(change);
   await publish();
 }`,
    outcome: 'BLOCK',
    summary: 'This would bypass your approval requirement and apply changes directly.',
    newState: [
      'No changes will occur'
    ],
    nextStep: 'Xable generated the safest available patch candidate. Review it before applying.',
    blockReasons: [
      'Your workspace requires approval before applying',
      'This action violates your safety policy'
    ]
  }
];

// Pick demo change deterministically based on description or quiz intent
function pickDemoChange(description: string): DemoChange {
  const lower = description.toLowerCase();
  if (lower.includes('deploy') || lower.includes('auto') || lower.includes('skip') || lower.includes('bypass')) {
    return DEMO_CHANGES[2]; // BLOCK example
  }
  if (lower.includes('api') || lower.includes('server') || lower.includes('valid') || lower.includes('backend')) {
    return DEMO_CHANGES[1];
  }
  return DEMO_CHANGES[0]; // Default: dashboard fix
}

export const AgentProposalFlow: React.FC<AgentProposalFlowProps> = ({
  isOpen,
  onClose,
  onExecutionComplete,
  preselectedChange,
  scenarioData,
  forkSuggestion,
  isForkConnected
}) => {
  const [step, setStep] = useState<ProposalStep>('create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [proposal, setProposal] = useState({
    actionType: 'contribution' as const,
    description: '',
    targetArtifactHashes: [] as string[]
  });

  const [simulation, setSimulation] = useState<{
    expectedOutcome: SimulationOutcome;
    reasons: string[];
  } | null>(null);

  const [demoChange, setDemoChange] = useState<DemoChange | null>(preselectedChange || null);

  // Use fork suggestion if available, otherwise use demo change
  const activeChange = forkSuggestion ? {
    title: forkSuggestion.title,
    file: forkSuggestion.file,
    diff: forkSuggestion.diff,
    outcome: forkSuggestion.outcome,
    summary: forkSuggestion.summary,
    newState: forkSuggestion.newState,
    nextStep: forkSuggestion.nextStep,
    blockReasons: forkSuggestion.blockReasons
  } as DemoChange : demoChange;

  const [showDiff, setShowDiff] = useState(false);

  const [proposalStatus, setProposalStatus] = useState<{
    status: ProposalStatus;
    reasons: string[];
    decisionPackage?: DecisionPackageV1;
  } | null>(null);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setStep('create');
      setError('');
      setProposal({
        actionType: 'contribution',
        description: '',
        targetArtifactHashes: []
      });
      setSimulation(null);
      setProposalStatus(null);
      setDemoChange(null);
      setShowDiff(false);
    }
  }, [isOpen]);

  const handleCreateProposal = () => {
    if (!proposal.description.trim()) {
      setError('Please describe your proposed action');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate proposal creation
    setTimeout(() => {
      try {
        setStep('simulate');
        setLoading(false);
        handleSimulate();
      } catch (err) {
        setError('Unable to create proposal. Please try again.');
        setLoading(false);
      }
    }, 500);
  };

  const handleSimulate = () => {
    setLoading(true);

    // Call agent gateway simulation
    setTimeout(() => {
      try {
        const change = activeChange || preselectedChange || pickDemoChange(proposal.description);
        setDemoChange(change);
        setSimulation({
          expectedOutcome: change.outcome,
          reasons: change.outcome === 'ALLOW'
            ? ['Authority check passed', 'Action type is permitted', 'No conflicts detected']
            : change.blockReasons || ['Action not permitted']
        });
        setStep('review');
        setLoading(false);
      } catch (err) {
        setError('Simulation failed. Please try again or contact support.');
        setLoading(false);
      }
    }, 1000);
  };

  // Auto-advance to simulate when a preselected change is provided
  React.useEffect(() => {
    if (isOpen && preselectedChange && step === 'create') {
      setProposal((p) => ({ ...p, description: preselectedChange.title }));
      setStep('simulate');
      handleSimulate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselectedChange]);

  // Build DecisionPackageV1 from proposal data
  const buildDecisionPackage = (): DecisionPackageV1 => {
    if (!demoChange) {
      throw new Error('No demo change available to build package');
    }

    const proposalId = `proposal_${Date.now()}`;
    const contentHash = `sha256:${proposalId.substring(0, 16)}_${Math.random().toString(36).substring(2, 10)}`;

    return {
      packageId: proposalId,
      createdAt: new Date().toISOString(),
      narrative: demoChange.summary,
      claims: [
        {
          id: 'claim-1',
          text: demoChange.title,
          status: 'supported',
          supportingText: demoChange.newState
        }
      ],
      contradictions: [],
      timeline: [
        {
          id: 'event-1',
          timestamp: new Date().toISOString(),
          description: `Proposal created: ${demoChange.title}`,
          order: 0,
          isConflictPoint: false
        },
        {
          id: 'event-2',
          timestamp: new Date().toISOString(),
          description: 'Simulation completed: SAFE TO REVIEW',
          order: 1,
          isConflictPoint: false
        }
      ],
      diff: {
        before: demoChange.diff.split('@@')[0] || 'Original code',
        after: demoChange.diff.split('@@')[demoChange.diff.split('@@').length - 1] || 'Modified code',
        changes: demoChange.newState
      },
      decision: {
        outcome: 'Ready for Review',
        reasoning: simulation?.reasons || ['Authority check passed', 'Action type is permitted', 'No conflicts detected']
      },
      verification: {
        contentHash,
        isValid: true
      }
    };
  };

  const handleApproveProposal = () => {
    setLoading(true);
    setError('');

    // Move to validate step for safety checks before building package
    setTimeout(() => {
      setStep('validate');
      // Auto-trigger validation after showing loading UI
      setTimeout(() => {
        handleValidateAndBuild();
      }, 500);
    }, 500);
  };

  const handleValidateAndBuild = () => {
    setLoading(true);
    setError('');

    // Perform safety validation before building decision package
    setTimeout(() => {
      try {
        // Safety check: Ensure simulation outcome is ALLOW
        if (simulation?.expectedOutcome !== 'ALLOW') {
          setError('Proposal cannot be validated: simulation outcome is BLOCKED');
          setLoading(false);
          return;
        }

        // Safety check: Ensure demo change exists
        if (!demoChange) {
          setError('Proposal cannot be validated: no change data available');
          setLoading(false);
          return;
        }

        // Build decision package
        const decisionPackage = buildDecisionPackage();
        const status = {
          status: 'VALID' as const,
          reasons: [
            'Simulation outcome: ALLOW',
            'Safety validation passed',
            'Gateway evaluation successful',
            'Decision package built'
          ],
          decisionPackage
        };
        setProposalStatus(status);
        setStep('result');
        setLoading(false);

        // Store package in sessionStorage for viewer to load
        sessionStorage.setItem('xable_proposal_package', JSON.stringify(decisionPackage));
      } catch (err) {
        setError('Validation failed. Please try again or contact support.');
        setLoading(false);
      }
    }, 1000);
  };

  const handleRejectProposal = () => {
    onClose();
  };

  // Guard: don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        className="w-full max-w-2xl max-h-[90vh] bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-xl shadow-2xl border border-gray-700 overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Brain className="w-5 h-5 text-purple-400" />
            <h2 className="text-xl font-semibold">Agent Proposal Flow</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            {[
              { id: 'create', label: 'Create' },
              { id: 'simulate', label: 'Simulate' },
              { id: 'review', label: 'Review' },
              { id: 'result', label: 'Result' }
            ].map((s, index) => {
              const currentStepIndex = ['create', 'simulate', 'review', 'result'].indexOf(step);
              const stepIndex = index;
              const isCompleted = stepIndex < currentStepIndex;
              const isCurrent = stepIndex === currentStepIndex;

              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isCompleted
                          ? 'bg-green-600 text-white'
                          : isCurrent
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {isCompleted ? <CheckCircle size={16} /> : index + 1}
                    </div>
                    <span
                      className={`text-xs mt-1 ${
                        isCurrent ? 'text-purple-400' : isCompleted ? 'text-green-400' : 'text-gray-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {index < 3 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        isCompleted ? 'bg-green-600' : 'bg-gray-700'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-700 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {step === 'create' && (
            <div className="space-y-4">
              {/* Fork Context */}
              {scenarioData && (
                <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Github className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-purple-300">Connected fork</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">{scenarioData.repoUrl}</p>
                  <div className="flex items-center space-x-2 mb-2">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-gray-300">Scenario: {scenarioData.scenarioId}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Files read: {scenarioData.files.length}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Action Type
                </label>
                <select
                  value={proposal.actionType}
                  onChange={(e) => setProposal({ ...proposal, actionType: e.target.value as any })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white"
                >
                  <option value="contribution">Create Contribution</option>
                  <option value="satisfy_obligation">Satisfy Obligation</option>
                  <option value="propose_exchange">Propose Exchange</option>
                  <option value="verify_payment">Verify Payment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={proposal.description}
                  onChange={(e) => setProposal({ ...proposal, description: e.target.value })}
                  placeholder="Describe what you want to do..."
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white h-32"
                />
              </div>
              <button
                onClick={handleCreateProposal}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <span>Simulate Proposal</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}

          {step === 'simulate' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
              <p className="text-gray-300">Simulating proposal outcome...</p>
              <p className="text-gray-500 text-sm mt-2">Checking authority, policy, and gateway rules</p>
            </div>
          )}

          {step === 'review' && simulation && (
            <div className="space-y-4">
              {/* Concrete proposed change */}
              {demoChange && (
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Proposed change</div>
                      <div className="text-white font-semibold">{demoChange.title}</div>
                      <div className="text-xs text-purple-400 font-mono mt-1">{demoChange.file}</div>
                    </div>
                    <button
                      onClick={() => setShowDiff(!showDiff)}
                      className="text-xs text-purple-400 hover:text-purple-300 whitespace-nowrap"
                    >
                      {showDiff ? 'Hide diff' : 'Show diff'}
                    </button>
                  </div>
                  {showDiff && (
                    <pre className="mt-3 p-3 bg-black/60 border border-gray-800 rounded text-xs text-gray-300 overflow-x-auto font-mono whitespace-pre">
                      {demoChange.diff.split('\n').map((line, i) => {
                        let color = 'text-gray-400';
                        if (line.startsWith('+') && !line.startsWith('+++')) color = 'text-green-400';
                        else if (line.startsWith('-') && !line.startsWith('---')) color = 'text-red-400';
                        else if (line.startsWith('@@')) color = 'text-purple-400';
                        return <div key={i} className={color}>{line || ' '}</div>;
                      })}
                    </pre>
                  )}
                </div>
              )}

              {/* Visually unavoidable ALLOW/BLOCK badge */}
              <div className={`p-6 rounded-lg border-2 ${
                simulation.expectedOutcome === 'ALLOW'
                  ? 'bg-green-900/30 border-green-500'
                  : 'bg-red-900/30 border-red-500'
              }`}>
                <div className="flex items-center justify-center space-x-3 mb-4">
                  {simulation.expectedOutcome === 'ALLOW' ? (
                    <>
                      <CheckCircle className="w-8 h-8 text-green-400" />
                      <span className="text-2xl font-bold text-green-400">
                        SAFE TO REVIEW ✓
                      </span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-8 h-8 text-red-400" />
                      <span className="text-2xl font-bold text-red-400">
                        BLOCKED — You're protected
                      </span>
                    </>
                  )}
                </div>
                <div className="text-center text-sm text-gray-400 mb-4">
                  {simulation.expectedOutcome === 'ALLOW'
                    ? 'This is simulated — nothing has applied yet.'
                    : 'Nothing applied. Your project is unchanged.'}
                </div>
                {demoChange && (
                  <div className="mb-4 text-sm text-gray-300">
                    {demoChange.summary}
                  </div>
                )}
                {demoChange && demoChange.newState.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">New state</div>
                    <div className="space-y-1">
                      {demoChange.newState.map((item, i) => (
                        <div key={i} className="flex items-start space-x-2 text-sm">
                          <div className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            simulation.expectedOutcome === 'ALLOW' ? 'bg-green-400' : 'bg-red-400'
                          }`} />
                          <span className="text-gray-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Why</div>
                  {simulation.reasons.map((reason, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        simulation.expectedOutcome === 'ALLOW' ? 'bg-green-400' : 'bg-red-400'
                      }`} />
                      <span className="text-gray-300">{reason}</span>
                    </div>
                  ))}
                </div>
                {demoChange && (
                  <div className="mt-4 pt-4 border-t border-gray-700 text-sm">
                    <span className="text-gray-500">Next step: </span>
                    <span className="text-gray-300">{demoChange.nextStep}</span>
                  </div>
                )}
              </div>

              {simulation.expectedOutcome === 'ALLOW' && (
                <div className="p-4 bg-purple-900/20 border border-purple-700 rounded-lg">
                  <p className="text-sm text-gray-300">
                    This is a deterministic simulation. The actual execution will match this outcome.
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                {simulation.expectedOutcome === 'ALLOW' ? (
                  <>
                    <button
                      onClick={handleRejectProposal}
                      className="flex-1 bg-gray-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        console.log('[AgentProposalFlow] Validate button clicked');
                        handleApproveProposal();
                      }}
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <span>Validate & Build Package</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleRejectProposal}
                    className="w-full bg-gray-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          )}


          {step === 'result' && proposalStatus && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg border ${
                proposalStatus.status === 'VALID'
                  ? 'bg-green-900/20 border-green-700'
                  : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center space-x-2 mb-3">
                  {proposalStatus.status === 'VALID' ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-semibold ${
                    proposalStatus.status === 'VALID' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    Admissible Proposal: {proposalStatus.status === 'VALID' ? 'Ready for Review' : proposalStatus.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {proposalStatus.reasons.map((reason, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        proposalStatus.status === 'VALID' ? 'bg-green-400' : 'bg-red-400'
                      }`} />
                      <span className="text-gray-300">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              {proposalStatus.status === 'VALID' && proposalStatus.decisionPackage && (
                <>
                  <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm text-gray-300">
                      <ShieldCheck className="w-4 h-4 text-purple-400" />
                      <span>Decision package built. No execution performed.</span>
                    </div>
                  </div>

                  {/* Review in Xable Button */}
                  <button
                    onClick={() => {
                      window.location.href = '/viewer';
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#00d4aa] to-[#00a8cc] text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Review in Xable</span>
                  </button>

                  {/* Phase 7: Patch Output Buttons */}
                  <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
                    <p className="text-sm text-gray-300 mb-3">Patch output</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          if (demoChange) {
                            navigator.clipboard.writeText(demoChange.diff);
                            console.log('[Patch] Copied to clipboard');
                          }
                        }}
                        className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                      >
                        <span>Copy Patch</span>
                      </button>
                      <button
                        onClick={() => {
                          if (demoChange) {
                            const blob = new Blob([demoChange.diff], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `xable-patch-${Date.now()}.diff`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                            console.log('[Patch] Downloaded');
                          }
                        }}
                        className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                      >
                        <span>Download Patch</span>
                      </button>
                      {scenarioData && (
                        <a
                          href={`${scenarioData.repoUrl}/tree/main/${scenarioData.scenarioId.replace(/\s+/g, '-').toLowerCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                        >
                          <span>Open Scenario on GitHub</span>
                          <Github className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Phase 8: Security Copy */}
                  <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                    <p className="text-xs text-blue-300">
                      Xable is reading only this selected public fork and only the files needed for this scenario. It cannot commit, push, merge, or deploy.
                    </p>
                  </div>
                </>
              )}

              <button
                onClick={() => {
                  console.log('[AgentProposalFlow] Done button clicked');
                  onClose();
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Done
              </button>
            </div>
          )}

          {/* Fallback: if result step but no proposalStatus, show close button */}
          {step === 'result' && !proposalStatus && (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">No result available</p>
              <button
                onClick={() => {
                  console.log('[AgentProposalFlow] Fallback close clicked');
                  onClose();
                }}
                className="bg-gray-700 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
